import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, FlipHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlatform } from "@/contexts/PlatformContext";

export default function QRScanner({ onScan, onError, isActive = true, locked = false }) {
  const { isApp } = usePlatform();
  const [isScanning, setIsScanning] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [hasPermission, setHasPermission] = useState(null);

  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const lastCodeRef = useRef(null);
  const lastCodeTimeRef = useRef(0);
  const isLockedRef = useRef(false);
  const isScanningRef = useRef(false);
  const camerasRef = useRef([]);
  const currentCameraIndexRef = useRef(0);
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    isScanningRef.current = isScanning;
  }, [isScanning]);

  useEffect(() => {
    camerasRef.current = cameras;
  }, [cameras]);

  useEffect(() => {
    currentCameraIndexRef.current = currentCameraIndex;
  }, [currentCameraIndex]);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    isLockedRef.current = locked;
  }, [locked]);

  const stopScanning = useCallback(async () => {
    if (!html5QrCodeRef.current || !isScanningRef.current) return;

    try {
      await html5QrCodeRef.current.stop();
      html5QrCodeRef.current.clear();
    } catch {}

    html5QrCodeRef.current = null;
    isScanningRef.current = false;
    setIsScanning(false);
  }, []);

  const startScanning = useCallback(async () => {
    if (!camerasRef.current.length || isScanningRef.current) return;

    try {
      const cameraId = camerasRef.current[currentCameraIndexRef.current]?.id;
      if (!cameraId) return;

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        cameraId,
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decodedText) => {
          if (isLockedRef.current) return;

          // Ignore duplicate reads of the same code within 3 seconds.
          const now = Date.now();
          if (decodedText === lastCodeRef.current && now - lastCodeTimeRef.current < 3000) return;

          isLockedRef.current = true;
          lastCodeRef.current = decodedText;
          lastCodeTimeRef.current = now;
          onScanRef.current?.(decodedText);
        },
        () => {}
      );

      isScanningRef.current = true;
      setIsScanning(true);
    } catch (err) {
      html5QrCodeRef.current = null;
      onErrorRef.current?.(err?.message || "Erro ao iniciar câmera");
    }
  }, []);

  useEffect(() => {
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (!devices?.length) {
          setHasPermission(false);
          return;
        }

        setCameras(devices);
        const backIdx = devices.findIndex((device) => {
          const label = device.label.toLowerCase();
          return label.includes("back") || label.includes("traseira") || label.includes("environment");
        });
        if (backIdx !== -1) setCurrentCameraIndex(backIdx);
        setHasPermission(true);
      })
      .catch(() => setHasPermission(false));

    return () => {
      stopScanning();
    };
  }, [stopScanning]);

  useEffect(() => {
    if (isActive && cameras.length > 0 && !isScanning) {
      startScanning();
      return;
    }

    if (!isActive && isScanning) {
      stopScanning();
    }
  }, [cameras.length, isActive, isScanning, startScanning, stopScanning]);

  const resetAndScan = useCallback(() => {
    lastCodeRef.current = null;
    lastCodeTimeRef.current = 0;
    isLockedRef.current = false;

    if (!isScanningRef.current) {
      startScanning();
    }
  }, [startScanning]);

  const switchCamera = useCallback(async () => {
    if (cameras.length <= 1) return;

    await stopScanning();
    const newIndex = (currentCameraIndex + 1) % cameras.length;
    setCurrentCameraIndex(newIndex);
    setTimeout(() => {
      startScanning();
    }, 300);
  }, [cameras.length, currentCameraIndex, startScanning, stopScanning]);

  if (hasPermission === false) {
    return (
      <div className="text-center p-6 bg-zinc-800/50 rounded-lg border border-red-500/30">
        <CameraOff className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-400 font-medium mb-2">Câmera não disponível</p>
        <p className="text-zinc-400 text-sm">
          Permita o acesso à câmera nas configurações do {isApp ? "app" : "navegador"}.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className="relative bg-black rounded-lg overflow-hidden"
        style={{ aspectRatio: "1/1", maxWidth: "400px", margin: "0 auto" }}
      >
        <div id="qr-reader" ref={scannerRef} className="w-full h-full" />
        {isScanning && !locked && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 border-2 border-[#fccf1f] rounded-lg animate-pulse" />
            </div>
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <span className="bg-black/70 text-[#fccf1f] px-4 py-2 rounded-full text-sm font-medium">
                Posicione o QR code no quadrado
              </span>
            </div>
          </div>
        )}
        {(!isScanning || locked) && (
          <div className="absolute inset-0 bg-zinc-900 flex items-center justify-center">
            <div className="text-center">
              <Camera className="w-16 h-16 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">{locked ? "QR lido" : "Câmera pausada"}</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-center gap-3 flex-wrap">
        {locked ? (
          <Button
            onClick={resetAndScan}
            className="bg-[#fccf1f] hover:bg-[#e6bc1c] text-black"
            data-testid="read-another-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Ler outro
          </Button>
        ) : (
          <Button
            onClick={() => {
              if (isScanning) {
                stopScanning();
              } else {
                startScanning();
              }
            }}
            className={isScanning ? "bg-red-600 hover:bg-red-700" : "bg-[#fccf1f] hover:bg-[#e6bc1c] text-black"}
            data-testid="toggle-camera-btn"
          >
            {isScanning ? (
              <>
                <CameraOff className="w-4 h-4 mr-2" />
                Pausar
              </>
            ) : (
              <>
                <Camera className="w-4 h-4 mr-2" />
                Iniciar Câmera
              </>
            )}
          </Button>
        )}

        {cameras.length > 1 && !locked && (
          <Button
            onClick={switchCamera}
            variant="outline"
            className="border-zinc-600 text-white hover:bg-zinc-700"
            disabled={!isScanning}
            data-testid="switch-camera-btn"
          >
            <FlipHorizontal className="w-4 h-4 mr-2" />
            Trocar
          </Button>
        )}
      </div>

      {cameras.length > 0 && (
        <p className="text-center text-zinc-500 text-xs">
          {cameras[currentCameraIndex]?.label || "Desconhecida"}
          {cameras.length > 1 && ` (${currentCameraIndex + 1}/${cameras.length})`}
        </p>
      )}
    </div>
  );
}
